import { useEffect, useLayoutEffect, useRef } from "react";

const isMac = typeof window !== "undefined" && /Mac|iPod|iPhone|iPad/.test(window.navigator.platform);

const INPUT_THRESHOLD_MS = 50;

type ShortcutHandler = (event: KeyboardEvent) => void;

interface ShortcutConfig {
  handler: ShortcutHandler;
  ignoreInputRules?: boolean;
}

type ShortcutValue = ShortcutHandler | ShortcutConfig;

interface KeyboardShortcuts {
  [key: string]: ShortcutValue;
}

interface Options {
  enabled?: boolean;
  preventDefault?: boolean;
  ignoreInputGlobal?: boolean;
}


export const useKeyboardShortcuts = (
  shortcuts: KeyboardShortcuts,
  options: Options = {}
) => {
  const {
    enabled = true,
    preventDefault = true,
    ignoreInputGlobal = false
  } = options;

  const shortcutsRef = useRef(shortcuts);
  const lastKeyTimeRef = useRef(0);

  useLayoutEffect(() => {
    shortcutsRef.current = shortcuts;
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const now = Date.now();
      const timeSinceLastKey = now - lastKeyTimeRef.current;
      lastKeyTimeRef.current = now;

      // we need this, otherwhise the scanner will trigger the shortcuts
      if (timeSinceLastKey < INPUT_THRESHOLD_MS) {
        return;
      }

      const keys: string[] = [];

      if (event.ctrlKey) keys.push("ctrl");
      if (event.metaKey) keys.push("meta");
      if (event.altKey) keys.push("alt");
      if (event.shiftKey) keys.push("shift");

      const isMod = isMac ? event.metaKey : event.ctrlKey;

      keys.push(event.key.toLowerCase());

      const combo = keys.join("+");

      const modCombo = isMod ? `mod+${event.key.toLowerCase()}` : null;

      const currentShortcuts = shortcutsRef.current;

      const match = currentShortcuts[combo] || (modCombo ? currentShortcuts[modCombo] : null);

      if (!match) {
        return;
      }

      const config = typeof match === "function" ? { handler: match } : match;

      const target = event.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (isInput && !ignoreInputGlobal && !config.ignoreInputRules) {
        if (event.key !== "Escape") {
          return;
        }
      }

      if (preventDefault) {
        event.preventDefault();
      }

      config.handler(event);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, preventDefault, ignoreInputGlobal]);
};